import { fetchWithTimeout, UPLOAD_TIMEOUT_MS } from '../api/fetchWithTimeout';
import { getToken, cachePut, cacheKeys, cacheGet, cacheDelete } from './db';
import { reportError } from '../telemetry/report';

/**
 * FACE SCANS TAKEN WITHOUT SIGNAL (17 Oct).
 *
 * Owner: "face scan cannot be skipped ... in any way." A phone that cannot
 * reach the office used to let the driver carry on with no scan at all and try
 * again at the next stop -- with the face scan at every stop switched on, that
 * stop simply had none (Jesus, 15-16 Oct, 3 stops). Now the scan is TAKEN
 * anyway, kept on the phone, and sent when signal returns, the same way the
 * delivery photos already are. The office still gets the match result; later.
 *
 * Kept in the existing 'cache' store under 'idscan:' keys rather than a new
 * store: a new store needs a database version change, and a phone still holding
 * the old version open would block the upgrade -- the one thing that must never
 * stop a delivery from saving.
 */
const API_BASE = import.meta.env.VITE_API_BASE_URL as string;
const PREFIX = 'idscan:';
const MAX_AGE_MS = 48 * 60 * 60 * 1000;

export interface PendingScan {
  id: string;
  taskId: string | null;
  trigger: 'route_start' | 'random_stop' | 'manual';
  takenAt: string;          // ISO, when the driver took it
  bytes: ArrayBuffer;
  type: string;
  tries: number;
}

export async function saveScanForLater(blob: Blob, taskId: string | null,
  trigger: PendingScan['trigger']): Promise<void> {
  const scan: PendingScan = {
    id: crypto.randomUUID(), taskId, trigger, takenAt: new Date().toISOString(),
    bytes: await blob.arrayBuffer(), type: blob.type || 'image/jpeg', tries: 0,
  };
  await cachePut(PREFIX + scan.id, scan);
  reportError('face_scan_saved_offline', 'identity', `task ${taskId ?? '-'}`);
}

export async function pendingScanCount(): Promise<number> {
  try { return (await cacheKeys()).filter((k) => k.startsWith(PREFIX)).length; } catch { return 0; }
}

/** A scan for this stop is still on the phone, waiting for signal (134). */
export async function hasPendingScanFor(taskId: string): Promise<boolean> {
  try {
    for (const k of (await cacheKeys()).filter((x) => x.startsWith(PREFIX))) {
      const scan = await cacheGet<PendingScan>(k);
      if (scan?.taskId === taskId) return true;
    }
  } catch { /* unknown: the server decides */ }
  return false;
}

/*
 * STOPS WAITING FOR THEIR FACE SCAN (134). The office refused a completion
 * because no scan reached it. The delivery stays queued on the phone; the stop
 * opens straight to the scan, and the delivery goes as soon as it is taken.
 */
const NEED_KEY = 'jw.scanNeeded';
function needList(): string[] {
  try { return JSON.parse(localStorage.getItem(NEED_KEY) ?? '[]') as string[]; } catch { return []; }
}
export function markScanNeeded(taskId: string): void {
  try { localStorage.setItem(NEED_KEY, JSON.stringify([...new Set([...needList(), taskId])])); } catch { /* fine */ }
}
export function scanNeeded(taskId: string): boolean { return needList().includes(taskId); }
export function clearScanNeeded(taskId: string): void {
  try { localStorage.setItem(NEED_KEY, JSON.stringify(needList().filter((x) => x !== taskId))); } catch { /* fine */ }
}

let flushing = false;

/** Send every saved scan. Called with every queue flush; never throws. */
export async function flushScans(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    const token = await getToken();
    if (!token) return;
    const keys = (await cacheKeys()).filter((k) => k.startsWith(PREFIX));
    for (const key of keys) {
      const scan = await cacheGet<PendingScan>(key);
      if (!scan) { await cacheDelete(key); continue; }
      if (Date.now() - Date.parse(scan.takenAt) > MAX_AGE_MS) {
        reportError('face_scan_dropped', 'identity', `task ${scan.taskId ?? '-'} tries ${scan.tries}`);
        await cacheDelete(key);
        continue;
      }
      try {
        await sendOne(scan, token);
        await cacheDelete(key);
      } catch (err) {
        await cachePut(key, { ...scan, tries: scan.tries + 1 });
        if ((err as Error).message === 'auth') return;   // signed out: wait for sign-in
        return;                                            // no signal: try again later
      }
    }
  } catch {
    /* Never the reason anything else fails. */
  } finally {
    flushing = false;
  }
}

async function sendOne(scan: PendingScan, token: string): Promise<void> {
  const authed = (path: string, body: unknown) => fetchWithTimeout(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const sign = await authed('/identity/sign', { contentType: scan.type });
  if (sign.status === 401 || sign.status === 403) throw new Error('auth');
  if (!sign.ok) throw new Error(`sign_${sign.status}`);
  const { uploadUrl, objectPath, requiredHeaders } = await sign.json() as
    { uploadUrl: string; objectPath: string; requiredHeaders?: Record<string, string> };
  const put = await fetchWithTimeout(uploadUrl, {
    method: 'PUT',
    headers: requiredHeaders ?? { 'Content-Type': scan.type },
    body: new Blob([scan.bytes], { type: scan.type }),
  }, UPLOAD_TIMEOUT_MS);
  if (!put.ok) throw new Error(`upload_${put.status}`);
  const check = await authed('/identity/check', {
    objectPath, trigger: scan.trigger, taskId: scan.taskId, takenAt: scan.takenAt,
  });
  if (check.status === 401 || check.status === 403) throw new Error('auth');
  if (!check.ok) throw new Error(`check_${check.status}`);
}
